// UniversalField.tsx
"use client";

import { PasswordField } from "layouts/PasswordField/PasswordField";
import { PinField } from "layouts/PinField/PinField";
import RadioField, { RadioOption } from "layouts/RadioField/RadioField";
import SelectField, { SelectOption } from "layouts/SelectField/SelectField";
import TextField from "layouts/TextField/TextField";
import {
  UniversalSelect,
  UniversalSelectItem,
} from "layouts/UniversalSelect/UniversalSelect";
import * as React from "react";

export type FieldTarget = "customerParams" | "customerDetails" | "body";

export type AllowedCharsType = "numeric" | "alpha" | "alphanumeric" | RegExp;

export type FieldOption = {
  label: string;
  value: string;
  icon?: React.ReactNode;
  description?: string;
  default?: boolean;
};

export interface FieldValidations {
  minLength?: number;
  maxLength?: number;
  allowedChars?: AllowedCharsType;
  regex?: string | RegExp;
}

export interface ApiMap {
  target: FieldTarget;
  key: string;
  mapValue?: (value: any, ctx: Record<string, any>) => string;
}

export interface FieldConfig {
  id: string;
  label?: string;
  as?: "input" | "password" | "select" | "combobox" | "radio" | "pin";
  type?: string; // for input
  placeholder?: string;
  required?: boolean;
  options?: FieldOption[]; // for select / radio
  validations?: FieldValidations;
  defaultValue?: any;
  api?: ApiMap | ApiMap[];
  // add: internal meta (original biller paramName)
  meta?: {
    billerParamName?: string;
  };
}

export interface UniversalFieldProps {
  field: FieldConfig;
  value?: any;
  onChange?: (value: any) => void;
  error?: string;
  helper?: string;
}

export const NewUniversalField: React.FC<UniversalFieldProps> = ({
  field,
  value,
  onChange,
  error,
  helper,
}) => {
  const {
    id,
    label,
    as = "input",
    // type = "text",
    placeholder,
    required,
    options = [],
    validations,
    defaultValue,
  } = field;

  const selectOptions: SelectOption[] = options.map((o) => ({
    label: o.label,
    value: o.value,
  }));

  const radioOptions: RadioOption[] = options.map((o) => ({
    label: o.label,
    value: o.value,
    disabled: (o as any).disabled,
  }));

  const comboItems: UniversalSelectItem[] = options.map((o) => ({
    label: o.label,
    value: o.value,
  }));

  switch (as) {
    case "password":
      return (
        <PasswordField
          id={id}
          label={label}
          required={required}
          value={value}
          defaultValue={defaultValue}
          error={error}
          helperText={helper}
          placeholder={placeholder}
          onChange={onChange}
        />
      );

    case "select":
      return (
        <SelectField
          id={id}
          label={label}
          required={required}
          value={value}
          defaultValue={defaultValue}
          error={error}
          helperText={helper}
          placeholder={placeholder}
          options={selectOptions}
          onChange={onChange}
        />
      );

    case "radio":
      return (
        <RadioField
          id={id}
          label={label}
          required={required}
          value={value}
          defaultValue={defaultValue}
          error={error}
          helperText={helper}
          options={radioOptions}
          onChange={onChange}
        />
      );

    case "pin":
      return (
        <PinField
          id={id}
          label={label}
          required={required}
          value={value}
          defaultValue={defaultValue}
          error={error}
          helperText={helper}
          length={validations?.maxLength ?? 4}
          onChange={onChange}
        />
      );

    case "combobox":
      return (
        <UniversalSelect
          id={id}
          label={label ?? ""}
          value={value}
          defaultValue={defaultValue}
          items={comboItems}
          multiple={false}
          error={error}
          placeholder={placeholder}
          onChange={onChange}
        />
      );

    case "input":
    default:
      return (
        <TextField
          id={id}
          label={label}
          required={required}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder}
          error={error}
          helperText={helper}
          maxLength={validations?.maxLength}
          allowedChars={validations?.allowedChars}
          onChange={onChange}
        />
      );
  }
};

// export const NewUniversalField: React.FC<UniversalFieldProps> = ({
//   field,
//   value,
//   onChange,
//   error,
//   helper,
// }) => {
//   const {
//     id,
//     label,
//     as = "input",
//     type = "text",
//     placeholder,
//     required,
//     options = [],
//     validations,
//     defaultValue,
//   } = field;

//   // map FieldOption -> SelectOption / RadioOption / UniversalSelectItem
//   const selectOptions: SelectOption[] = options.map((o) => ({
//     label: o.label,
//     value: o.value,
//   }));

//   const radioOptions: RadioOption[] = options.map((o) => ({
//     label: o.label,
//     value: o.value,
//     disabled: (o as any).disabled,
//   }));

//   const comboItems: UniversalSelectItem[] = options.map((o) => ({
//     label: o.label,
//     value: o.value,
//   }));

//   switch (as) {
//     case "password":
//       return (
//         <PasswordField
//           id={id}
//           label={label}
//           required={required}
//           value={value}
//           defaultValue={defaultValue}
//           error={error}
//           helperText={helper}
//           placeholder={placeholder}
//           onChange={onChange}
//         />
//       );

//     case "select":
//       return (
//         <SelectField
//           id={id}
//           label={label}
//           required={required}
//           value={value}
//           defaultValue={defaultValue}
//           error={error}
//           helperText={helper}
//           placeholder={placeholder}
//           options={selectOptions}
//           onChange={onChange}
//         />
//       );

//     case "radio":
//       return (
//         <RadioField
//           id={id}
//           label={label}
//           required={required}
//           value={value}
//           defaultValue={defaultValue}
//           error={error}
//           helperText={helper}
//           options={radioOptions}
//           onChange={onChange}
//         />
//       );

//     case "pin":
//       return (
//         <PinField
//           id={id}
//           label={label}
//           required={required}
//           value={value}
//           defaultValue={defaultValue}
//           error={error}
//           helperText={helper}
//           length={validations?.maxLength ?? 4}
//           onChange={onChange}
//         />
//       );

//     case "combobox":
//       return (
//         <UniversalSelect
//           id={id}
//           label={label ?? ""}
//           value={value}
//           defaultValue={defaultValue}
//           items={comboItems}
//           multiple={false}
//           error={error}
//           placeholder={placeholder}
//           onChange={onChange}
//           //onChange={(val) => onChange?.(val)}
//           // search / infinite-scroll ka config agar chahiye to yaha set karo
//         />
//       );

//     case "input":
//     default:
//       return (
//         <TextField
//           id={id}
//           label={label}
//           required={required}
//           value={value}
//           defaultValue={defaultValue}
//           placeholder={placeholder}
//           error={error}
//           helperText={helper}
//           maxLength={validations?.maxLength}
//           allowedChars={validations?.allowedChars}
//           onChange={onChange}
//         />
//       );
//   }
// };
